import { useLazyQuery } from '@apollo/client';
import { useContext, useState, useEffect } from 'react';
import { cleanUrl, sortCountries, sortRegions } from './utils';
import { PlacesContext } from './provider';
import { GET_SUGGESTION, GET_GOOGLE_PLACES_API_TEXT_SEARCH, GET_COUNTRIES, GET_LOCATION, GET_POPULAR_AREAS, GET_LEGACY_DESTINATION_LIST, GET_GOOGLE_PLACES_API_AUTOCOMPLETE, GET_GOOGLE_PLACES_API_DETAIL, } from './queries';
/**
 * Hook to get free-text suggestions from the places API
 */
export const useSuggestions = () => {
    const [getSuggestion] = useLazyQuery(GET_SUGGESTION);
    const [getGooglePlacesApiTextSearch] = useLazyQuery(GET_GOOGLE_PLACES_API_TEXT_SEARCH);
    const [getGooglePlacesApiAutocomplete] = useLazyQuery(GET_GOOGLE_PLACES_API_AUTOCOMPLETE);
    const { language, brand } = useContext(PlacesContext);
    return async (searchTerm, limit, googleFallbackMethod) => {
        const { data } = await getSuggestion({
            variables: {
                input: {
                    brandName: brand,
                    languageCode: language,
                    searchText: searchTerm,
                    searchMethod: brand === 'novasol' ? 'exact' : 'fuzzy',
                    limit,
                },
            },
        });
        const placesResults = data?.getSuggestion
            ?.filter((l) => l.translated_name !== null)
            ?.map(({ id, translated_name, legacy_id, drupal_id, type, parent, country, }) => ({
            id,
            name: translated_name,
            legacyId: legacy_id,
            drupalId: drupal_id,
            type,
            parentName: parent?.translation?.name,
            countryName: country?.translation?.name,
        })) || [];
        if (placesResults?.length) {
            return placesResults;
        }
        if (googleFallbackMethod === 'textsearch') {
            const { data: googlePlacesData } = await getGooglePlacesApiTextSearch({
                variables: {
                    input: {
                        searchText: searchTerm,
                        languageCode: language,
                        brandName: brand,
                    },
                },
            });
            return ((googlePlacesData?.getGooglePlacesApiTextSearch).map((l) => ({
                ...l,
                countryName: l.country_name,
                parentName: l.parent_name,
            })) || []);
        }
        if (googleFallbackMethod === 'autocomplete') {
            const { data: googlePlacesData } = await getGooglePlacesApiAutocomplete({
                variables: {
                    input: {
                        searchText: searchTerm,
                        languageCode: language,
                        brandName: brand,
                    },
                },
            });
            return ((googlePlacesData?.getGooglePlacesApiAutocomplete).map((l) => ({
                ...l,
                name: l.translated_name,
            })) || []);
        }
        return [];
    };
};
/**
 * Hook to get get destination data from the places API
 * If given a queryId, it will return the location data for that id
 * If not given a queryId, it will return the countries and their children
 */
export const useFetchDestination = () => {
    const [getCountries] = useLazyQuery(GET_COUNTRIES);
    const [getLocation] = useLazyQuery(GET_LOCATION);
    const [getPopularAreas] = useLazyQuery(GET_POPULAR_AREAS);
    const { language, brand } = useContext(PlacesContext);
    return async (queryId, addPopularAreas = true, inDestinationList = true) => {
        // Get popular aras
        const { data: popularAreas } = addPopularAreas
            ? await getPopularAreas({
                variables: {
                    brandName: brand,
                    countryId: queryId ?? '',
                },
            })
            : { data: [] };
        // Get locations
        if (queryId) {
            const { data } = await getLocation({
                variables: {
                    brandName: brand,
                    languageCode: language,
                    queryId,
                },
            });
            if (!data?.getLocation) {
                return {};
            }
            const { id, parent_id: parentId, translation, legacy_id: legacyId, drupal_id: drupalId, children, touristic_terms: touristicTerms, } = data.getLocation;
            return {
                id,
                parentId: parentId || '0',
                name: translation.name,
                legacyId,
                drupalId,
                locations: [
                    ...children
                        ?.filter((c) => c.translation !== null &&
                        (inDestinationList
                            ? c.translation.in_destination_list === true
                            : true))
                        ?.map(({ translation: childrenTranslation, parent_id, legacy_id, drupal_id, __typename, ...rest }) => ({
                        ...rest,
                        legacyId: legacy_id,
                        drupalId: drupal_id,
                        parentId: parent_id,
                        name: childrenTranslation.name,
                        type: __typename,
                    })),
                    ...touristicTerms
                        ?.filter((t) => t.translation !== null &&
                        (inDestinationList
                            ? t.translation.in_destination_list === true
                            : true))
                        ?.map(({ translation: touristicTranslation, parent_id, legacy_id: t_legacy_id, drupal_id, __typename, ...rest }) => ({
                        ...rest,
                        legacyId: t_legacy_id,
                        drupalId: drupal_id,
                        parentId: null,
                        name: touristicTranslation.name,
                        type: __typename,
                    })),
                ],
                ...(!parentId && {
                    popular: {
                        locations: popularAreas?.getPopularAreas?.map((area) => ({
                            ...area,
                            legacyId: area.legacy_id,
                            drupalId: area.drupal_id,
                            type: area.__typename,
                        })),
                    },
                }),
            };
        }
        // Get countries
        const { data } = await getCountries({
            variables: { brand, language },
        });
        return {
            id: null,
            name: null,
            parentId: null,
            locations: data?.getCountries
                ?.filter((country) => country.translation !== null &&
                (inDestinationList
                    ? country.translation.in_destination_list === true
                    : true))
                ?.map(({ has_children, translation, name, legacy_id, drupal_id, parent_id, __typename, ...location }) => ({
                ...location,
                parentId: parent_id,
                legacyId: legacy_id,
                drupalId: drupal_id,
                name: translation.name,
                type: __typename,
                has_children,
            })),
            popular: {
                locations: popularAreas?.getPopularAreas?.map((area) => ({
                    ...area,
                    legacyId: area.legacy_id,
                    drupalId: area.drupal_id,
                    type: area.__typename,
                })),
            },
        };
    };
};
/**
 * Hook to get a hardcoded list of destinations for the UK brands.
 * This is used for the legacy destination list. Clean up when A/B testing is done.
 */
export const useFetchDestinationForLegacyList = (returnLegacyList = false) => {
    const [legacyListState, setLegacyListState] = useState(null);
    const [getLegacyDestinationList] = useLazyQuery(GET_LEGACY_DESTINATION_LIST);
    const [getPopularAreas] = useLazyQuery(GET_POPULAR_AREAS);
    const { language, brand } = useContext(PlacesContext);
    async function getPlacesDataForLegacyList() {
        // Get countries and children destinations
        const { data: destinations } = await getLegacyDestinationList({
            variables: { brandName: brand, languageCode: language },
        });
        // Get popular areas given a country id
        const fetchPopularAreasByCountry = async (countryId) => {
            const { data: popularAreas } = await getPopularAreas({
                variables: {
                    brandName: brand,
                    countryId,
                },
            });
            return popularAreas?.getPopularAreas ?? [];
        };
        const legacyPlacesList = (await Promise.all(destinations?.getCountries
            ?.filter((country) => country?.translation?.in_destination_list === true)
            ?.map(async (country) => {
            const popularAreas = await fetchPopularAreasByCountry(country?.id);
            return {
                regionName: country?.translation?.name, // England ...
                regionValue: country?.translation?.name,
                regionId: country?.legacy_id,
                placesId: country?.id,
                url: `/${cleanUrl(country?.translation?.name)}`,
                regions: [
                    {
                        regionName: `All of ${country?.translation?.name}`, // All of England ...
                        regionValue: country?.translation?.name,
                        regionId: country?.legacy_id,
                        placesId: country?.id,
                        url: `/${cleanUrl(country?.translation?.name)}`,
                    },
                    ...country?.children
                        ?.filter((region) => region?.translation?.in_destination_list === true)
                        ?.map((region) => ({
                        regionName: region?.translation?.name, // South West England ...
                        regionValue: region?.translation?.name,
                        regionId: region?.legacy_id,
                        placesId: region?.id,
                        url: `/${cleanUrl(country?.translation?.name)}`,
                        regions: [
                            {
                                regionName: `All of ${region?.translation?.name}`, // All of South West England ...
                                regionValue: region?.translation?.name,
                                regionId: region?.legacy_id,
                                placesId: region?.id,
                                url: `/${cleanUrl(country?.translation?.name)}`,
                            },
                            ...region?.children
                                ?.filter((area) => area?.translation?.in_destination_list === true)
                                ?.map((area) => ({
                                regionName: area?.translation?.name, // Cornwall ...
                                regionValue: area?.translation?.name,
                                regionId: area?.legacy_id,
                                placesId: area?.id,
                                url: `/${cleanUrl(country?.translation?.name)}/${cleanUrl(area?.translation?.name)}`,
                            })),
                        ],
                    })),
                ],
                popular: popularAreas.length > 0
                    ? {
                        title: `Popular in ${country?.translation?.name}`, // Popular in England
                        locations: popularAreas?.map((popularArea) => ({
                            regionName: popularArea?.name,
                            regionValue: popularArea?.name,
                            regionId: popularArea?.legacy_id,
                            placesId: popularArea?.id,
                            url: `/${cleanUrl(country?.translation?.name)}/${cleanUrl(popularArea?.name)}`,
                        })),
                    }
                    : null,
            };
        })).catch((error) => {
            // eslint-disable-next-line no-console
            console.error('MFE-Common Places-API useFetchDestinationForLegacyList', error);
            return [];
        })) || null;
        try {
            // Custom sort countries first.
            const sortedLegacyPlacesList = sortCountries(legacyPlacesList);
            // Custom sort the regions within countries.
            const orderedRegionNames = [
                [
                    'All of England',
                    'South West England',
                    'Southern England',
                    'Central England',
                    'East Anglia',
                    'North West England',
                    'North East England',
                    'Yorkshire',
                ],
                [
                    'All of Scotland',
                    'Southern Scotland',
                    'Central Scotland',
                    'Northern Scotland',
                ],
                ['All of Wales', 'South Wales', 'Mid Wales', 'North Wales'],
                ['All of Channel Islands'],
                ['All of Isle of Man'],
            ];
            for (let i = 0; i < sortedLegacyPlacesList.length; i += 1) {
                sortedLegacyPlacesList[i].regions = sortRegions(sortedLegacyPlacesList[i], orderedRegionNames[i]);
            }
            setLegacyListState(sortedLegacyPlacesList);
        }
        catch (error) {
            // eslint-disable-next-line no-console
            console.error('MFE-Common Places-API useFetchDestinationForLegacyList', error);
            return [];
        }
        return null;
    }
    useEffect(() => {
        if (returnLegacyList) {
            getPlacesDataForLegacyList();
        }
    }, [returnLegacyList]);
    return legacyListState;
};
/**
 * Hook to get details from a googleID.
 * GoogleIDs are returned from the useSuggestion method if GoogleSearchMethods is set.
 */
export const useResolveGooglePlacesID = (googleId = '') => {
    const [data, setData] = useState('');
    const [getGooglePlacesApiDetail] = useLazyQuery(GET_GOOGLE_PLACES_API_DETAIL);
    const { language } = useContext(PlacesContext);
    async function getGoogleID(googleID) {
        try {
            const { data: result } = await getGooglePlacesApiDetail({
                variables: {
                    input: {
                        googlePlacesApiId: googleID,
                        languageCode: language,
                    },
                },
            });
            setData(result?.getGooglePlacesApiDetail?.coordinates_location);
        }
        catch (error) {
            // eslint-disable-next-line no-console
            console.error('MFE-Common Places-API useResolveGooglePlacesID', error);
        }
    }
    useEffect(() => {
        if (googleId !== '') {
            getGoogleID(googleId);
        }
    }, [googleId]);
    return data;
};
//# sourceMappingURL=hooks.js.map